import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTrackedLocalStorage } from "./use-tracked-browser-storage";

/**
 * Hook for managing channel selection with automatic organization scoping.
 *
 * Features:
 * - Selects channels and persists them in URL parameters and localStorage
 * - Falls back to previously selected channel when URL has no channelId
 * - Synchronizes between URL and localStorage
 */
export function useChannelSelection(): {
  selectedChannelType: string | undefined;
  setSelectedChannelType: (channelName: string | null) => void;
} {
  const params = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Manage selected channel in localStorage with automatic organization scoping
  const [selectedChannelName, setSelectedChannelName] =
    useTrackedLocalStorage<string>(
      "channels::explorer::selected-channel::active",
      "",
    );

  // Get channel name from URL path param
  const channelNameFromPath = params.channelId; // URL param is still called channelId for backwards compatibility
  const storedChannelName = selectedChannelName || undefined;

  const currentChannelName = channelNameFromPath || storedChannelName;

  // Handle channel selection
  const setSelectedChannelNameHandler = useCallback(
    (channelName: string | null) => {
      if (channelName) {
        // Use the new URL pattern and preserve query (e.g., ?view)
        const target = {
          pathname: `/channels/${channelName}/explorer`,
          search: location.search,
        };
        const isSame =
          location.pathname === target.pathname &&
          location.search === (target.search || "");
        if (!isSame) {
          navigate(target, { replace: true });
        }

        // Also update localStorage for persistence across page loads
        setSelectedChannelName(channelName);
      } else {
        // Navigate to base explorer page
        navigate("/channels/explorer", { replace: true });

        // Clear from localStorage
        setSelectedChannelName("");
      }
    },
    [navigate, setSelectedChannelName, location.pathname, location.search],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // If we have a stored selection but no URL param, update the URL
    if (!channelNameFromPath && storedChannelName) {
      // For explorer pages, use the new URL pattern
      if (location.pathname === "/channels/explorer") {
        navigate(`/channels/${storedChannelName}/explorer`, { replace: true });
      }
    }
  }, [channelNameFromPath, storedChannelName, navigate, location.pathname]);

  return {
    selectedChannelType: currentChannelName,
    setSelectedChannelType: setSelectedChannelNameHandler,
  };
}
